import { Forecast } from "../../types";
import { WeatherIcon } from "../weatherIcon";
import { WindArrow } from "../windArrow";
import { compassDirection, forecastHourConditions, parseNumber } from "../../utils/conditions";
import { dayLabel, hourLabel, localTime } from "../../utils/areaForecast";
import { RatingButton } from "../ratingButton";
import { RatingsHelpLink } from "../ratingsExplainer";
import { SectionCard } from "./sectionCard";
import { IconClock } from "./icons";
import "./hourlyCard.css";

// Gusts at or above this are called out in the wind row
const GUSTY_MPH = 30;

interface HourlyCardProps {
    forecast: Forecast;
    now: number;
}

export function HourlyCard({ forecast, now }: HourlyCardProps) {
    const tz = forecast.timezone;
    const hours = forecast.hourly?.data ?? [];
    if (hours.length === 0) {
        return null;
    }

    return (
        <SectionCard
            className="area-page__hourly"
            icon={<IconClock />}
            title="Hourly"
            aside={<><span>% precip</span><span>mph wind</span><RatingsHelpLink>climb rating</RatingsHelpLink></>}
        >
            {/* Focusable so keyboard users can scroll it sideways */}
            <div className="hourly" tabIndex={0} role="region" aria-label="Hourly forecast, scrolls sideways">
                <ol className="hourly__list">
                    {hours.map((dp, i) => {
                        const date = localTime(dp.time, tz).date;
                        const newDay = i > 0 && date !== localTime(hours[i - 1].time, tz).date;
                        const temperature = parseNumber(dp.temperature);
                        const precip = parseNumber(dp.precipProbability);
                        const wind = parseNumber(dp.windSpeed);
                        const gust = parseNumber(dp.windGust);
                        const gusty = gust !== undefined && gust >= GUSTY_MPH;
                        // No arrow for calm air: a direction for 0 mph means nothing
                        const bearing = parseNumber(dp.windBearing);
                        const direction = bearing !== undefined && wind !== undefined && Math.round(wind) > 0 ? bearing : undefined;
                        const classes = ["hourly__hour"];
                        if (i === 0) classes.push("hourly__hour--now");
                        if (newDay) classes.push("hourly__hour--new-day");

                        return (
                            <li key={dp.time} className={classes.join(" ")}>
                                <span className="hourly__day">{i === 0 || newDay ? dayLabel(dp.time, tz, now, true) : ""}</span>
                                <span className="hourly__time">{i === 0 ? "Now" : hourLabel(dp.time, tz)}</span>
                                <WeatherIcon code={dp.icon} size={28} />
                                <span className="hourly__temp">{temperature !== undefined ? `${Math.round(temperature)}°` : "–"}</span>
                                <span className="hourly__precip">
                                    {precip !== undefined ? `${Math.round(precip * 100)}%` : "–"}
                                    <span className="cw-sr-only"> chance of precipitation</span>
                                </span>
                                <span className={"hourly__wind" + (gusty ? " hourly__wind--gusty" : "")}>
                                    {direction !== undefined && <WindArrow bearing={direction} />}
                                    {wind !== undefined ? Math.round(wind) : "–"}
                                    <span className="cw-sr-only">
                                        {" "}mph wind{direction !== undefined ? ` from the ${compassDirection(direction)}` : ""}{gusty ? `, gusts ${Math.round(gust!)}` : ""}
                                    </span>
                                </span>
                                <RatingButton
                                    conditions={forecastHourConditions(forecast, dp)}
                                    context={i === 0 ? "Right now" : `${dayLabel(dp.time, tz, now)} ${hourLabel(dp.time, tz)}`}
                                />
                            </li>
                        );
                    })}
                </ol>
            </div>
        </SectionCard>
    );
}
